import { bbox, center as turfCenter } from "@turf/turf";
import type {
  FeatureCollection,
  GeoJSON,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
} from "geojson";

import type { CoordsType } from "~/lib/map/types";

export const getCenterCoordsFromGeoJson = (geoJson: GeoJSON): CoordsType | undefined => {
  switch (geoJson.type) {
    case "FeatureCollection": {
      const featureCollection = geoJson as FeatureCollection;
      const centerPoint = turfCenter(featureCollection);
      const [longitude, latitude] = centerPoint.geometry.coordinates;
      return { lat: latitude, lng: longitude };
    }

    case "Point": {
      const point = geoJson as Point;
      return { lat: point.coordinates[1], lng: point.coordinates[0] };
    }

    case "LineString":
    case "MultiPoint": {
      const lineStringOrMultiPoint = geoJson as LineString | MultiPoint;
      const coords = lineStringOrMultiPoint.coordinates;
      const avgCoords = coords
        .reduce((acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]], [0, 0])
        .map((coord) => coord / coords.length);
      return { lat: avgCoords[1], lng: avgCoords[0] };
    }

    case "Polygon":
    case "MultiLineString": {
      const polygonOrMultiLineString = geoJson as Polygon | MultiLineString;
      const bboxCoords = bbox(polygonOrMultiLineString);
      return { lat: (bboxCoords[1] + bboxCoords[3]) / 2, lng: (bboxCoords[0] + bboxCoords[2]) / 2 };
    }

    case "MultiPolygon": {
      const multiPolygon = geoJson as MultiPolygon;
      const centers = multiPolygon.coordinates.map((polygon) => {
        const polyBbox = bbox({ type: "Polygon", coordinates: polygon });
        return [(polyBbox[0] + polyBbox[2]) / 2, (polyBbox[1] + polyBbox[3]) / 2];
      });
      const avgCenter = centers
        .reduce((acc, center) => [acc[0] + center[0], acc[1] + center[1]], [0, 0])
        .map((coord) => coord / centers.length);
      return { lat: avgCenter[1], lng: avgCenter[0] };
    }

    default:
      return undefined;
  }
};
